import { useEffect, useState } from 'react';
import * as St from './SgBackgroundDesign.styled';
import { CustomColorPalette } from '@/components/color-palette/CustomColorPalette';
import { CustomRatioImage } from '@/components/image/CustomRatioImage';
import { useApiHook } from '@/views/mypage/hooks/useApiHook';
import { useUserPageContextValueHook } from '@/contexts/UserPageContextProvider';
import CustomBlockButton from '@/components/button/CustomBlockButton';
import { useChangeUserPageBackgroundDesignMutation } from '@/hooks/ReactQuery/UserPageReactQuery/UserPageReactQuery';
import { ProBadge, ProUpsell, usePagePro } from '@/views/mypage/components/FdSubscription/proPlan';
import { BACKGROUND_GRADIENTS } from '../../premiumDesign';

export function SgBackgroundDesign({
    onRefreshPreviewViewer
}) {
    const userPageContextValueHook = useUserPageContextValueHook();

    const apiHook = useApiHook();

    const changeUserPageBackgroundDesignMutation = useChangeUserPageBackgroundDesignMutation();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [designThemeList, setDesignThemeList] = useState(null);
    const isPro = usePagePro();
    const savedImageUrl = userPageContextValueHook?.userPage?.backgroundImageUrl;
    const [backgroundType, setBackgroundType] = useState(
        userPageContextValueHook?.userPage?.backgroundType === 'GRADIENT' ? 'GRADIENT'
            : (!userPageContextValueHook?.userPage?.backgroundType || userPageContextValueHook?.userPage?.backgroundType === 'IMAGE') ? 'IMAGE' : 'HEX'
    ) // IMAGE, HEX, GRADIENT(Pro)
    const [backgroundGradient, setBackgroundGradient] = useState(userPageContextValueHook?.userPage?.backgroundGradient || BACKGROUND_GRADIENTS[0].value);
    const [backgroundImageUrl, setBackgroundImageUrl] = useState(userPageContextValueHook?.userPage?.backgroundImageUrl || '');
    const [backgroundColorHexChangeModeOpen, setBackgroundColorHexChangeModeOpen] = useState(false);
    const [backgroundColorHex, setBackgroundColorHex] = useState(userPageContextValueHook?.userPage?.backgroundColorHex || '#ffffff');

    const handleReqFetchDesignThemeList = async () => {
        const result = await apiHook.reqFetchDesignThemeList({ params: null, headers: null });

        if (result?.content) {
            setDesignThemeList(result?.content);
        }
    }

    const handleReqChangeBackgroundDesign = async () => {
        let body = {
            id: userPageContextValueHook?.userPage?.id,
            backgroundType: backgroundType,
            backgroundImageUrl: backgroundImageUrl,
            backgroundColorHex: backgroundColorHex,
        }

        if (backgroundType === 'GRADIENT') {
            // 단색은 그대로 두어 Pro 가 끝나면 그 색으로 보이게 한다
            body.backgroundImageUrl = '';
            body.backgroundGradient = backgroundGradient;
        } else if (!backgroundType || !['IMAGE', 'HEX'].includes(backgroundType) || backgroundType === 'HEX') {
            body.backgroundType = 'HEX';
            body.backgroundImageUrl = '';
            body.backgroundColorHex = backgroundColorHex || '#FFFFFF';
        } else if (backgroundType === 'IMAGE') {
            body.backgroundType = 'IMAGE';
            body.backgroundImageUrl = backgroundImageUrl || '';
            body.backgroundColorHex = '#FFFFFF';
        }

        changeUserPageBackgroundDesignMutation.mutate({ body }, {
            onSuccess: () => {
                onRefreshPreviewViewer();
            }
        })
    }

    const toggleBackgroundColorHexChangeModeOpen = (bool) => {
        setBackgroundColorHexChangeModeOpen(bool)
    }


    const handleChangeBackgroundType = (type) => {
        setBackgroundType(type);
    }

    const handleChangeBackgroundImageUrl = (backgroundImageUrl) => {
        setBackgroundImageUrl(backgroundImageUrl);
    }

    const handleChangeBackgroundColorHex = (hex) => {
        setBackgroundColorHex(hex);
    }

    useEffect(() => {
        handleReqFetchDesignThemeList();
    }, []);

    return (
        <>
            <St.Container>
                <St.HeaderWrapper>
                    <div className='headerBox' onClick={() => setDropdownOpen(!dropdownOpen)}>
                        <div className='title'>배경 디자인</div>
                        <div className='icon'>
                            <CustomRatioImage>
                                {dropdownOpen ?
                                    <CustomRatioImage.Image src='/assets/icons/expandLess_000000.svg' />
                                    :
                                    <CustomRatioImage.Image src='/assets/icons/expandMore_000000.svg' />
                                }
                            </CustomRatioImage>
                        </div>
                    </div>
                </St.HeaderWrapper>
                {dropdownOpen &&
                    <>
                        <St.TypeSelectorWrapper>
                            <CustomBlockButton
                                type='button'
                                className={`${!backgroundType || backgroundType === 'HEX' ? 'active' : ''}`}
                                onClick={() => handleChangeBackgroundType('HEX')}
                            >
                                커스텀 색상
                            </CustomBlockButton>
                            <CustomBlockButton
                                type='button'
                                className={`${backgroundType === 'IMAGE' ? 'active' : ''}`}
                                onClick={() => handleChangeBackgroundType('IMAGE')}
                            >
                                기본 이미지
                            </CustomBlockButton>
                            <CustomBlockButton
                                type='button'
                                className={`${backgroundType === 'GRADIENT' ? 'active' : ''}`}
                                onClick={() => handleChangeBackgroundType('GRADIENT')}
                            >
                                <span className='inline-flex items-center gap-1'>프리미엄 <ProBadge /></span>
                            </CustomBlockButton>
                        </St.TypeSelectorWrapper>
                        {(backgroundType === 'GRADIENT') &&
                            <div className='mt-5 px-5'>
                                {!isPro &&
                                    <div className='mb-3'>
                                        <ProUpsell title="프리미엄 배경" description="그라디언트 · 메시 · 패턴 · 움직이는 배경으로 페이지를 꾸밀 수 있어요." />
                                    </div>
                                }
                                <div className='grid grid-cols-5 gap-2'>
                                    {BACKGROUND_GRADIENTS.map(gradient => (
                                        <button
                                            key={gradient.value}
                                            type='button'
                                            disabled={!isPro}
                                            aria-label={gradient.value}
                                            onClick={() => setBackgroundGradient(gradient.value)}
                                            className={`aspect-[3/4] rounded-xl ring-offset-2 transition ${gradient.animation || ''} ${backgroundGradient === gradient.value ? 'ring-2 ring-gray-900' : 'ring-1 ring-black/10'} ${!isPro ? 'cursor-not-allowed opacity-50' : ''}`}
                                            style={{ background: gradient.css }}
                                        />
                                    ))}
                                </div>
                            </div>
                        }
                        {(!backgroundType || backgroundType === 'HEX') &&
                            <St.ColorSelectorWrapper>
                                <div className='currentColorTitle'>현재 색상</div>
                                <div className='currentColor' onClick={() => toggleBackgroundColorHexChangeModeOpen(true)}>
                                    <div className='colorBox' style={{ backgroundColor: backgroundColorHex }}></div>
                                    <div className='colorHex'>{backgroundColorHex}</div>
                                </div>
                                {backgroundColorHexChangeModeOpen &&
                                    <CustomColorPalette
                                        open={backgroundColorHexChangeModeOpen}
                                        fromColor={backgroundColorHex}
                                        toggleOpenClose={toggleBackgroundColorHexChangeModeOpen}
                                        onConfirm={handleChangeBackgroundColorHex}
                                    />
                                }
                                <div className='sampleColorList'>
                                    {CUSTOM_COLOR_LIST?.map(customColor => {
                                        return (
                                            <div
                                                key={customColor?.hex}
                                                className='colorBox'
                                                style={{ backgroundColor: customColor?.hex }}
                                                onClick={() => handleChangeBackgroundColorHex(customColor?.hex)}
                                            ></div>
                                        );
                                    })}
                                </div>
                            </St.ColorSelectorWrapper>
                        }
                        {(backgroundType === 'IMAGE') &&
                            <St.ImageSelectorWrapper>
                                <div className='cardList'>
                                    {designThemeList?.map(designTheme => {
                                        // Pro 전용 테마는 Pro 만 고를 수 있다 (이미 쓰던 배경은 그대로 둘 수 있음 — 서버도 같은 규칙)
                                        const locked = designTheme?.isPro && !isPro && designTheme?.backgroundImageUrl !== savedImageUrl;
                                        return (
                                            <div
                                                className={`card relative ${designTheme?.backgroundImageUrl === backgroundImageUrl ? 'active' : ''} ${locked ? 'cursor-not-allowed opacity-50' : ''}`}
                                                key={designTheme?.id}
                                                onClick={() => !locked && handleChangeBackgroundImageUrl(designTheme?.backgroundImageUrl)}
                                            >
                                                <CustomRatioImage>
                                                    <CustomRatioImage.Image src={designTheme?.backgroundImageUrl} />
                                                </CustomRatioImage>
                                                {designTheme?.isPro && <ProBadge className="absolute left-1.5 top-1.5" />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </St.ImageSelectorWrapper>
                        }
                        <St.FooterWrapper>
                            <CustomBlockButton
                                type='button'
                                disabled={backgroundType === 'GRADIENT' && !isPro}
                                onClick={() => handleReqChangeBackgroundDesign()}
                            >
                                적용
                            </CustomBlockButton>
                        </St.FooterWrapper>
                    </>
                }
            </St.Container>
        </>
    );
}

const CUSTOM_COLOR_LIST = [
    {
        hex: '#FFFFFF',
    },
    {
        hex: '#F8EDE3',
    },
    {
        hex: '#DFD3C3',
    },
    {
        hex: '#D0B8A8',
    },
    {
        hex: '#EECAD5',
    },
    {
        hex: '#F1D3CE',
    },
    {
        hex: '#F6EACB',
    },
    {
        hex: '#D1E9F6',
    },
    {
        hex: '#E7E8D8',
    },
    {
        hex: '#CADABF',
    },
    {
        hex: '#31363F',
    },
    {
        hex: '#35374B',
    }
]